import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { S2C, TOOLS, defaultUserSettings } from '@shared/protocol.js';
import { api, type Pad, type User } from '@/lib/api';
import { PadConnection, type ChatMessage, type ConnectionState } from '@/lib/net';
import { PadEngine, type ToolState } from '@/canvas/engine';
import type { Op, Peer } from '@/canvas/types';
import {
  Alert, Modal, useStoredState, useToast, copyText,
} from './kit';
import { IconTrash, IconClose } from './icons';
import CommandBar from './pad/CommandBar';
import ToolDock from './pad/ToolDock';
import ActionDock from './pad/ActionDock';
import PresenceBar from './pad/PresenceBar';
import PadSettingsModal from './pad/PadSettingsModal';
import ChatPanel from './pad/ChatPanel';
import ReplayBar from './pad/ReplayBar';
import { downscaleImage } from '@/lib/image';
import { PAYWALL } from '@shared/plans.js';
import UpgradeModal from './UpgradeModal';

export interface PadPrefs {
  theme: 'light' | 'dark';
  background: 'plain' | 'dots' | 'grid' | 'lines';
  showCursors: boolean;
  showCursorNames: boolean;
  showPeerViewports: boolean;
  pressure: boolean;
  smoothing: boolean;
  wheelZooms: boolean;
  showPanFrontier: boolean;
  snapShapes: boolean;
  leftHanded: boolean;
  confirmClear: boolean;
  autosaveThumbnails: boolean;
  showFps: boolean;
}

const DEFAULT_PREFS: PadPrefs = {
  ...(defaultUserSettings() as unknown as PadPrefs),
  theme: 'light',
  background: 'dots',
  wheelZooms: false,
  showPanFrontier: true,
  snapShapes: true,
  leftHanded: false,
  showFps: false,
};

/** Friendly names for the shapes hold-to-snap can produce. */
const SNAP_NAMES: Record<string, string> = {
  line: 'straight line',
  rect: 'rectangle',
  ellipse: 'circle',
  triangle: 'triangle',
  diamond: 'diamond',
};

/** What to call the thing you just picked up. */
const SELECTION_LABELS: Record<string, string> = {
  image: 'Image',
  stroke: 'Stroke',
  shape: 'Shape',
  text: 'Text',
  stamp: 'Stamp',
};

const DEFAULT_TOOLS: ToolState = {
  tool: TOOLS.PEN,
  color: '#1a1859',
  size: 6,
  opacity: 1,
  stamp: '⭐',
  shape: 'rect',
  fillShapes: false,
  textSize: 28,
};

interface Props {
  user: User;
  pad: Pad;
  ticket: string;
  role: string;
  onLeave: () => void;
}

export default function PadScreen({
  user, pad: initialPad, ticket, role: initialRole, onLeave,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<PadEngine | null>(null);
  const connRef = useRef<PadConnection | null>(null);

  const [pad, setPad] = useState(initialPad);
  // The socket effect must not re-run when the pad object changes, so it reads
  // the id through a ref rather than closing over it.
  const padRef = useRef(initialPad);
  padRef.current = pad;
  const [role, setRole] = useState(initialRole);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [latency, setLatency] = useState(0);
  const [peers, setPeers] = useState<Peer[]>([]);
  const [history, setHistory] = useState({ canUndo: false, canRedo: false });
  const [hasContent, setHasContent] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [selection, setSelection] = useState<Op | null>(null);
  const [fps, setFps] = useState(0);
  /**
   * How many people fit in *this* pad. Comes from the server on WELCOME and is
   * the host's allowance, not yours — a free guest in a Pro host's sketchpad
   * sits in a room of five. Seeded from your own plan so the first paint is
   * right when you are the host.
   */
  const [seats, setSeats] = useState(user.limits.drawers);

  const [menuOpen, setMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showReplay, setShowReplay] = useState(false);
  /** Non-null while the upgrade screen is open; the string says what prompted it. */
  const [paywall, setPaywall] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [overview, setOverview] = useState(false);
  const [following, setFollowing] = useState<string | null>(null);
  const [textDraft, setTextDraft] = useState<{
    world: { x: number; y: number }; screen: { x: number; y: number }; value: string;
  } | null>(null);
  /** When the text field opened, so an immediate blur can be told from a real one. */
  const openedAt = useRef(0);

  const [prefs, setPrefs] = useStoredState<PadPrefs>('inkshare.prefs', DEFAULT_PREFS);
  const [tools, setTools] = useStoredState<ToolState>('inkshare.tools', DEFAULT_TOOLS);

  const toast = useToast();
  const isHost = pad.ownerId === user.id;
  const readOnly = role === 'spectator';

  const me = useMemo(
    () => ({ id: user.id, username: user.username, avatarColor: user.avatarColor }),
    [user.id, user.username, user.avatarColor],
  );

  // --- engine + connection, created once per pad session --------------------

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const engine = new PadEngine(host, me, {
      onHistoryChange: setHistory,
      onSceneChange: () => setHasContent(engine.scene.bounds() !== null),
      onSelectionChange: setSelection,
      onSnap: (shape) => toast(`Snapped to a ${SNAP_NAMES[shape] ?? shape} — keep moving to undo it`),
      onTextRequest: (world, screen) => {
        openedAt.current = Date.now();
        setTextDraft({ world, screen, value: '' });
      },
      onFps: setFps,
    }, initialRole === 'spectator');
    engineRef.current = engine;

    // Pad tickets last two minutes, so a reconnect needs a new one rather than
    // a replay of the one this screen was opened with.
    const conn = new PadConnection(
      ticket,
      setConnection,
      async () => (await api.openPad(padRef.current.id)).ticket,
    );
    connRef.current = conn;
    engine.conn = conn;

    const syncPeers = () => setPeers([...engine.peers.values()]);

    conn.on(S2C.WELCOME, (msg) => {
      setPad(msg.pad);
      setRole(msg.role);
      if (msg.limits?.maxMembers) setSeats(msg.limits.maxMembers);
      engine.readonly = msg.role === 'spectator';
      engine.peers.clear();
      for (const peer of msg.peers) engine.peers.set(peer.connectionId, peer as Peer);
      for (const live of msg.liveStrokes ?? []) {
        engine.peerStrokeBegin(live.strokeId, live.style, live.points, live.userId, live.connectionId);
      }
      engine.scene.putMany(msg.ops);
      // Ops arriving after a reconnect must not wipe what we already had.
      engine.invalidate();
      setMessages(msg.chat ?? []);
      syncPeers();
      setHasContent(engine.scene.bounds() !== null);
      // A brand new pad starts fully zoomed in at the origin; an existing one
      // opens on whatever is already there.
      if (!engine.scene.bounds()) engine.setCamera({ x: 0, y: 0, zoom: 8 });
      else engine.fitEverything();
    });

    conn.on(S2C.PEER_JOIN, (msg) => {
      engine.peers.set(msg.peer.connectionId, msg.peer);
      syncPeers();
      toast(`${msg.peer.user.username} joined`);
    });

    conn.on(S2C.PEER_LEAVE, (msg) => {
      const peer = engine.peers.get(msg.connectionId);
      engine.peers.delete(msg.connectionId);
      // Otherwise a half-drawn rectangle hangs on the canvas for everyone.
      engine.peerDraft(msg.connectionId, null);
      if (engine.following === msg.connectionId) {
        engine.stopFollowing();
        setFollowing(null);
      }
      syncPeers();
      if (peer) toast(`${peer.user.username} left`);
    });

    conn.on(S2C.CURSOR, (msg) => {
      const peer = engine.peers.get(msg.connectionId);
      if (peer) peer.cursor = msg.cursor;
    });

    conn.on(S2C.VIEWPORT, (msg) => {
      const peer = engine.peers.get(msg.connectionId);
      if (peer) peer.viewport = msg.viewport;
    });

    conn.on(S2C.LASER, (msg) => engine.peerLaser(msg.connectionId, msg.points));
    conn.on(S2C.DRAFT, (msg) => engine.peerDraft(msg.connectionId, msg.op ?? null));

    conn.on(S2C.STROKE_BEGIN, (msg) => {
      engine.peerStrokeBegin(msg.strokeId, msg.style, msg.points ?? [], msg.userId, msg.connectionId);
    });
    conn.on(S2C.STROKE_POINTS, (msg) => engine.peerStrokePoints(msg.strokeId, msg.points));
    conn.on(S2C.STROKE_END, (msg) => engine.peerStrokeEnd(msg.strokeId, msg.op ?? null));

    conn.on(S2C.OP, (msg) => {
      // Our own committed ops come back with a real sequence number.
      engine.applyRemoteOp(msg.op);
    });

    conn.on(S2C.OP_UPDATE, (msg) => engine.applyRemoteUpdate(msg.op));
    conn.on(S2C.OP_DELETE, (msg) => engine.applyDelete(msg.ids));
    conn.on(S2C.OP_RESTORE, (msg) => engine.applyRestore(msg.ids));

    conn.on(S2C.CLEAR, (msg) => {
      engine.applyDelete(msg.ids);
      if (msg.by !== user.id) toast(msg.scope === 'all' ? 'The sketchpad was cleared' : 'Someone cleared their drawing');
    });

    conn.on(S2C.CHAT, (msg) => {
      setMessages((prev) => [...prev.slice(-199), msg.message]);
      if (msg.message.userId !== user.id) setUnread((n) => n + 1);
    });

    conn.on(S2C.PAD_UPDATE, (msg) => setPad(msg.pad));

    conn.on(S2C.KICKED, () => setFatal('The host removed you from this sketchpad.'));

    conn.on(S2C.ERROR, (msg) => {
      if (msg.code === 'rate_limited') return;
      toast(msg.message ?? 'Something went wrong.', 'error');
    });

    conn.on('fatal', (msg) => {
      if (msg.code === 4005) setFatal('This sketchpad is full right now.');
      else if (msg.code === 4008) setFatal('The host removed you from this sketchpad.');
      else setFatal('Your invite expired. Open the sketchpad again from your list.');
    });

    conn.connect();

    // Presence chips only need a coarse refresh; cursors move far too fast to
    // drive React re-renders.
    const presenceTimer = window.setInterval(() => {
      syncPeers();
      setLatency(conn.latency);
      setFollowing(engine.following);
    }, 450);

    return () => {
      window.clearInterval(presenceTimer);
      conn.close();
      engine.destroy();
      engineRef.current = null;
      connRef.current = null;
    };
    // Deliberately runs once per pad session: `ticket` identifies the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket, me]);

  // --- push config into the engine ----------------------------------------

  useEffect(() => {
    engineRef.current?.setTools(tools);
  }, [tools]);

  useEffect(() => {
    engineRef.current?.setSettings({
      theme: prefs.theme,
      // The host picks the paper for the room; a personal choice overrides it.
      background: prefs.background,
      showCursors: prefs.showCursors && pad.settings.showPeerCursors,
      showCursorNames: prefs.showCursorNames,
      showPeerViewports: prefs.showPeerViewports && pad.settings.showPeerViewports,
      pressure: prefs.pressure,
      smoothing: prefs.smoothing,
      wheelZooms: prefs.wheelZooms,
      showPanFrontier: prefs.showPanFrontier,
      snapShapes: prefs.snapShapes,
      showFps: prefs.showFps,
    });
    document.documentElement.dataset.theme = prefs.theme;
  }, [prefs, pad.settings.showPeerCursors, pad.settings.showPeerViewports]);

  useEffect(() => {
    if (showChat) setUnread(0);
  }, [showChat, messages.length]);

  // Show the join code once, right after creating a pad, so the host sees it
  // even if they never open settings.
  useEffect(() => {
    if (isHost && pad.joinCode && pad.lastOpSeq === 0) setShowWelcome(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- autosave preview ----------------------------------------------------

  useEffect(() => {
    if (!prefs.autosaveThumbnails) return undefined;
    let lastVersion = -1;
    const timer = window.setInterval(() => {
      const engine = engineRef.current;
      if (!engine || engine.scene.version === lastVersion) return;
      lastVersion = engine.scene.version;
      const thumb = engine.exportThumbnail(420);
      if (!thumb) return;
      // The op log is already saved server-side; this only refreshes the
      // picture shown in everyone's sketchpad list.
      api.saveThumbnail(pad.id, thumb).catch(() => {});
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [pad.id, prefs.autosaveThumbnails]);

  // --- images: paste, drop, pick ------------------------------------------

  const insertImage = useCallback(async (file: File) => {
    // Checked before the file is read so a big photo is not decoded and
    // downscaled only to be refused. The server refuses it too — this is the
    // courtesy, not the control.
    if (!user.limits.images) {
      setPaywall(PAYWALL.images());
      return;
    }
    try {
      const { src, width, height } = await downscaleImage(file);
      const op = engineRef.current?.placeImage(src, width, height);
      // Switch to the select tool with the new image already picked up, so the
      // resize handles are under your fingers straight away.
      if (op) {
        setTools({ tool: TOOLS.SELECT });
        engineRef.current?.select(op.id);
      }
      toast('Image added — drag the handles to move or resize it.');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not read that image.', 'error');
    }
  }, [toast]);

  useEffect(() => {
    if (readOnly) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      const file = [...(e.clipboardData?.items ?? [])]
        .find((item) => item.type.startsWith('image/'))?.getAsFile();
      if (file) {
        e.preventDefault();
        insertImage(file);
      }
    };
    const onDrop = (e: DragEvent) => {
      const file = [...(e.dataTransfer?.files ?? [])].find((f) => f.type.startsWith('image/'));
      if (file) {
        e.preventDefault();
        insertImage(file);
      }
    };
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener('paste', onPaste);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragover', prevent);
    return () => {
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragover', prevent);
    };
  }, [insertImage, readOnly]);

  // --- keyboard shortcuts --------------------------------------------------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return;
      const engine = engineRef.current;
      if (!engine) return;
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) engine.redo();
        else engine.undo();
        return;
      }
      if (mod && e.key === '0') {
        e.preventDefault();
        engine.fitEverything();
        return;
      }
      if (mod) return;

      if (e.key === 'Escape') {
        engine.select(null);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && engine.selection) {
        e.preventDefault();
        engine.deleteSelection();
        return;
      }

      const map: Record<string, string> = {
        b: TOOLS.PEN,
        m: TOOLS.MARKER,
        h: TOOLS.HIGHLIGHTER,
        n: TOOLS.NEON,
        e: TOOLS.ERASER,
        x: TOOLS.OBJECT_ERASER,
        t: TOOLS.TEXT,
        f: TOOLS.FILL,
        s: TOOLS.STAMP,
        l: TOOLS.LASER,
        v: TOOLS.PAN,
        d: TOOLS.SELECT,
      };
      const tool = map[e.key.toLowerCase()];
      if (tool) {
        setTools({ tool });
        return;
      }
      // Shortcuts for the shapes people reach for most.
      const shape: Record<string, string> = { r: 'rect', o: 'ellipse', a: 'arrow', y: 'line' };
      const wanted = shape[e.key.toLowerCase()];
      if (wanted) {
        setTools({ tool: TOOLS.SHAPE, shape: wanted });
        return;
      }
      if (e.key >= '1' && e.key <= '6') {
        const sizes = [1.5, 3, 6, 12, 24, 44];
        setTools({ size: sizes[Number(e.key) - 1] });
        return;
      }
      if (e.key === '[') setTools({ size: Math.max(1, tools.size / 1.6) });
      if (e.key === ']') setTools({ size: Math.min(200, tools.size * 1.6) });
      if (e.key === 'Home') engine.returnToStart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setTools, tools.size]);

  // --- actions -------------------------------------------------------------

  const engine = () => engineRef.current;

  const exportImage = () => {
    const data = engine()?.exportPNG();
    if (!data) {
      toast('Nothing to save yet.', 'error');
      return;
    }
    const link = document.createElement('a');
    link.href = data;
    link.download = `${pad.title.replace(/[^\w -]+/g, '') || 'sketchpad'}.png`;
    link.click();
    toast('Saved as a PNG.');
  };

  const print = () => {
    const data = engine()?.exportPNG({ background: '#ffffff' });
    if (!data) {
      toast('Nothing to print yet.', 'error');
      return;
    }
    const win = window.open('', '_blank');
    if (!win) {
      toast('Your browser blocked the print window.', 'error');
      return;
    }
    win.document.write(
      `<title>${pad.title}</title><style>body{margin:0}img{width:100%}</style>`
      + `<img src="${data}" onload="window.print()">`,
    );
    win.document.close();
  };

  const share = async () => {
    if (!pad.joinCode) {
      toast('Only the host can share the join code.', 'error');
      return;
    }
    const url = `${window.location.origin}/#/join/${pad.joinCode}`;
    const text = `Draw with me on Ink Share — join code ${pad.joinCode}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: pad.title, text, url });
        return;
      } catch {
        // The user dismissed the sheet; fall through to copying.
      }
    }
    const ok = await copyText(`${text}\n${url}`);
    toast(ok ? 'Invite copied to your clipboard.' : 'Could not copy the invite.', ok ? 'default' : 'error');
  };

  const toggleOverview = () => {
    const next = !overview;
    setOverview(next);
    engine()?.setOverview(next);
    if (next && peers.length === 0) toast('Nobody else is here yet.');
  };

  /**
   * Tear the canvas down as soon as the session is over.
   *
   * The engine appends its canvases to the container itself, so React knows
   * nothing about them. When this component swaps to the "sketchpad closed"
   * screen React reuses the same div — and the canvases came along for the
   * ride, sitting over the whole viewport with pointer events enabled. The
   * button underneath was there and looked fine, but every click landed on a
   * canvas instead, which is why it appeared to do nothing.
   */
  useEffect(() => {
    if (!fatal) return;
    connRef.current?.close();
    connRef.current = null;
    engineRef.current?.destroy();
    engineRef.current = null;
  }, [fatal]);

  const commitText = () => {
    if (!textDraft) return;
    if (textDraft.value.trim()) engine()?.commitText(textDraft.world, textDraft.value);
    setTextDraft(null);
  };

  if (fatal) {
    return (
      <div className="screen">
        <div className="card center">
          <h2 style={{ marginTop: 0 }}>Sketchpad closed</h2>
          <Alert tone="error">{fatal}</Alert>
          <button type="button" className="btn btn--accent btn--block" onClick={onLeave}>
            Back to my sketchpads
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pad"
      data-hand={prefs.leftHanded ? 'left' : 'right'}
      data-menu={menuOpen ? 'open' : 'closed'}
    >
      <div className="pad__canvas" ref={hostRef} />

      <div className="pad__chrome">
        <div className="dock dock--tl">
          <CommandBar
            open={menuOpen}
            onToggle={() => setMenuOpen((v) => !v)}
            onSettings={() => { setShowSettings(true); setMenuOpen(false); }}
            onInsertImage={insertImage}
            onExport={exportImage}
            onChat={() => { setShowChat((v) => !v); setMenuOpen(false); }}
            onLeave={onLeave}
            onPrint={print}
            onShare={share}
            onReplay={() => {
              setMenuOpen(false);
              if (!user.limits.replay) setPaywall(PAYWALL.replay());
              else setShowReplay(true);
            }}
            chatOpen={showChat}
            chatEnabled={pad.settings.chatEnabled}
            unread={unread}
          />
        </div>

        <div className="dock dock--tc">
          <PresenceBar
            me={me}
            peers={peers}
            overview={overview}
            following={following}
            onToggleOverview={toggleOverview}
            onGoTo={(id) => {
              if (!engine()?.goToPeer(id)) toast('They have not moved around yet.');
            }}
            onFollow={(id) => {
              engine()?.goToPeer(id, { follow: true });
              setFollowing(id);
            }}
            onStopFollowing={() => { engine()?.stopFollowing(); setFollowing(null); }}
            isHost={isHost}
            onKick={(userId) => connRef.current?.kick(userId)}
            seats={seats}
            connection={connection}
            latency={latency}
            onInvite={share}
            canInvite={!!pad.joinCode}
          />
        </div>

        {!readOnly ? (
          <div className="dock dock--tr">
            <ToolDock
              tools={tools}
              onChange={setTools}
              laserEnabled={pad.settings.laserEnabled}
            />
          </div>
        ) : null}

        <div className="dock dock--br">
          <ActionDock
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            isHost={isHost}
            allowGuestClearAll={pad.settings.allowGuestClearAll}
            hasContent={hasContent}
            confirmClear={prefs.confirmClear}
            onUndo={() => engine()?.undo()}
            onRedo={() => engine()?.redo()}
            onClear={(scope) => engine()?.clear(scope)}
            onGoMyWork={() => {
              if (!engine()?.returnToMyWork()) toast('You have not drawn anything yet — here is the start.');
            }}
            onGoStart={() => engine()?.returnToStart()}
            onFitAll={() => engine()?.fitEverything()}
            onZoom={(factor) => engine()?.zoomBy(factor)}
          />
        </div>

        {showChat && pad.settings.chatEnabled ? (
          <ChatPanel
            messages={messages}
            myId={user.id}
            onSend={(body) => connRef.current?.sendChat(body)}
            onClose={() => setShowChat(false)}
          />
        ) : null}

        {showReplay && engineRef.current ? (
          <ReplayBar engine={engineRef.current} onClose={() => setShowReplay(false)} />
        ) : null}

        {selection && tools.tool === TOOLS.SELECT ? (
          <div className="pill selection-bar">
            <span className="chip__meta" style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
              {SELECTION_LABELS[selection.kind] ?? 'Item'} selected — drag to move, corners to resize
            </span>
            <span className="pill-sep" />
            <button
              type="button"
              className="pill-btn pill-btn--sm"
              onClick={() => engine()?.deleteSelection()}
              aria-label="Delete selection"
              title="Delete (⌫)"
            >
              <IconTrash size={18} />
            </button>
            <button
              type="button"
              className="pill-btn pill-btn--sm"
              onClick={() => engine()?.select(null)}
              aria-label="Deselect"
              title="Deselect (Esc)"
            >
              <IconClose size={18} />
            </button>
          </div>
        ) : null}

        {readOnly ? (
          <div className="hint-strip">
            This sketchpad is full, so you are watching. You will be able to draw when someone leaves.
          </div>
        ) : null}

        {overview ? (
          <div className="hint-strip">
            Everyone&apos;s screens are outlined. Tap a name above to jump to them.
          </div>
        ) : null}

        {prefs.showFps ? (
          <div
            className="badge"
            style={{ position: 'absolute', bottom: 'calc(var(--safe-b) + 14px)', left: 'calc(var(--safe-l) + 14px)' }}
          >
            {fps} fps · {latency}ms
          </div>
        ) : null}
      </div>

      {textDraft ? (
        <input
          className="text-input-floating"
          ref={(el) => el?.focus()}
          value={textDraft.value}
          style={{ left: textDraft.screen.x, top: textDraft.screen.y - 20 }}
          placeholder="Type, then press Enter"
          onChange={(e) => setTextDraft({ ...textDraft, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitText();
            if (e.key === 'Escape') setTextDraft(null);
          }}
          onBlur={(e) => {
            // Losing focus in the instant after mounting is the browser
            // finishing the click that opened this field, not the user moving
            // on. Committing on that blur is what made the field vanish
            // before a single character could be typed. Take the focus back.
            if (Date.now() - openedAt.current < 400) {
              e.target.focus();
              return;
            }
            commitText();
          }}
        />
      ) : null}

      {paywall !== null ? (
        <UpgradeModal user={user} reason={paywall} onClose={() => setPaywall(null)} />
      ) : null}

      {showSettings ? (
        <PadSettingsModal
          pad={pad}
          user={user}
          isHost={isHost}
          seats={seats}
          prefs={prefs}
          onPrefsChange={setPrefs}
          onPadChange={setPad}
          onClose={() => setShowSettings(false)}
          onLeave={onLeave}
        />
      ) : null}

      {showWelcome && pad.joinCode ? (
        <Modal
          title="Your sketchpad is ready"
          onClose={() => setShowWelcome(false)}
          footer={(
            <>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => { copyText(pad.joinCode!); toast('Join code copied.'); }}
              >
                Copy code
              </button>
              <button type="button" className="btn btn--accent" onClick={() => setShowWelcome(false)}>
                Start drawing
              </button>
            </>
          )}
        >
          <p style={{ marginTop: 0 }}>Share this code so your friends can draw with you.</p>
          <div className="codebox">
            <span className="codebox__code selectable">{pad.joinCode}</span>
          </div>
          <p className="tiny muted">
            It is always here under <strong>Settings → This pad</strong>, so there is nothing to write down.
            Everything you draw saves as you go.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
